import { gerarCalendario } from './liga';
import { clubesDaLiga, PAISES_MUNDO } from './ligas-mundo';
import type { EstadoJogo } from './jogo';
import type { LinhaMundo } from './ligas-mundo-sim';
import { Rng } from './rng';

/**
 * Calendário das ligas estrangeiras: cada uma tem a sua temporada de pontos corridos, que anda junto com as rodadas de liga do
 * Brasileirão (a rodada n da liga brasileira joga a rodada n de cada liga estrangeira). Os resultados saem de um modelo rápido de
 * gols pela diferença de força dos clubes (o mesmo de ligas-mundo-sim.ts), não do motor de partida: por enquanto são ligas só da CPU.
 *
 * O calendário NÃO é guardado no save: ele é refeito igual a cada vez a partir da semente do jogo, da temporada e da liga. O save
 * guarda só os placares já jogados (um número por jogo: gols do mandante × 100 + gols do visitante).
 */
export interface LigaMundoViva {
  /** temporada do jogo a que a liga pertence */
  temporada: number;
  /** quantas rodadas já foram jogadas */
  jogadas: number;
  /** placares por rodada, na ordem dos jogos do calendário da rodada */
  placares: number[][];
}

/** Rodadas que cabem na temporada do Brasileirão (38): turno e returno quando dá, senão só o turno. */
export function rodadasDaLiga(clubes: number): number {
  return 2 * (clubes - 1) <= 38 ? 2 * (clubes - 1) : clubes - 1;
}

const FORCA_PADRAO = 40;
const ESCALA_FORCA = 45;

interface CalendarioLiga {
  clubes: string[];
  forcas: number[];
  /** por rodada, os jogos [mandante, visitante] como índices em `clubes` */
  rodadas: [number, number][][];
}

const cacheCalendarios = new Map<string, CalendarioLiga>();

function hash(texto: string): number {
  let h = 2166136261;
  for (let i = 0; i < texto.length; i++) h = Math.imul(h ^ texto.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Calendário da liga na temporada (determinístico: semente do jogo + temporada + liga). */
export function calendarioDaLiga(semente: number, temporada: number, ligaId: string): CalendarioLiga {
  const chave = `${semente}|${temporada}|${ligaId}`;
  const guardado = cacheCalendarios.get(chave);
  if (guardado) return guardado;
  const clubes = clubesDaLiga(ligaId);
  const nomes = clubes.map((c) => c.nome);
  const forcas = clubes.map((c) => c.forca ?? FORCA_PADRAO);
  const rng = new Rng((semente * 31 + temporada * 1009 + hash(ligaId)) >>> 0);
  const ids = nomes.map((_, i) => i);
  const completo = gerarCalendario(ids, rng);
  const rodadas = completo.slice(0, rodadasDaLiga(nomes.length)).map((r) => r.map((c): [number, number] => [c.mandante, c.visitante]));
  const cal: CalendarioLiga = { clubes: nomes, forcas, rodadas };
  if (cacheCalendarios.size > 60) cacheCalendarios.clear();
  cacheCalendarios.set(chave, cal);
  return cal;
}

function poisson(lambda: number, rng: Rng): number {
  const limite = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k++;
    p *= rng.proximo();
  } while (p > limite && k < 12);
  return k - 1;
}

/** Placar de um jogo pelo modelo rápido (mandante com um pouco de vantagem). */
export function placarRapido(forcaCasa: number, forcaFora: number, rng: Rng): [number, number] {
  const dif = (forcaCasa - forcaFora) / ESCALA_FORCA;
  return [poisson(1.4 * Math.exp(dif), rng), poisson(1.1 * Math.exp(-dif), rng)];
}

/** As ligas estrangeiras do jogo (id de ligas-mundo.ts). */
export function idsDasLigasMundo(): string[] {
  return PAISES_MUNDO.flatMap((p) => p.ligas.map((l) => l.id));
}

/** Começa a temporada de todas as ligas estrangeiras (na temporada atual do jogo). */
export function iniciarLigasMundoVivas(e: EstadoJogo): void {
  const vivas: Record<string, LigaMundoViva> = {};
  for (const id of idsDasLigasMundo()) vivas[id] = { temporada: e.temporada, jogadas: 0, placares: [] };
  e.ligasMundoVivas = vivas;
}

function jogarRodada(e: EstadoJogo, ligaId: string, viva: LigaMundoViva): void {
  const cal = calendarioDaLiga(e.semente, viva.temporada, ligaId);
  const r = viva.jogadas;
  const jogos = cal.rodadas[r];
  if (!jogos) return;
  const rng = new Rng((e.semente * 7 + viva.temporada * 131 + hash(ligaId) + r * 977) >>> 0);
  viva.placares.push(jogos.map(([c, f]) => {
    const [gc, gf] = placarRapido(cal.forcas[c] as number, cal.forcas[f] as number, rng);
    return Math.min(gc, 99) * 100 + Math.min(gf, 99);
  }));
  viva.jogadas++;
}

/** Joga a próxima rodada de cada liga estrangeira (chamado quando uma rodada de liga do Brasileirão termina). */
export function avancarLigasMundo(e: EstadoJogo): void {
  for (const [id, viva] of Object.entries(e.ligasMundoVivas ?? {})) {
    if (viva.temporada === e.temporada) jogarRodada(e, id, viva);
  }
}

/** Classificação da liga com os jogos já disputados (pontos, vitórias, saldo, gols pró, nome). */
export function tabelaDaLigaMundo(e: EstadoJogo, ligaId: string): LinhaMundo[] | null {
  const viva = e.ligasMundoVivas?.[ligaId];
  if (!viva) return null;
  const cal = calendarioDaLiga(e.semente, viva.temporada, ligaId);
  const linhas = cal.clubes.map((clube): LinhaMundo => ({ clube, pts: 0, v: 0, e: 0, d: 0, gp: 0, gc: 0 }));
  viva.placares.forEach((rodada, r) => {
    rodada.forEach((placar, k) => {
      const jogo = cal.rodadas[r]?.[k];
      if (!jogo) return;
      const gc = Math.floor(placar / 100);
      const gf = placar % 100;
      const a = linhas[jogo[0]] as LinhaMundo;
      const b = linhas[jogo[1]] as LinhaMundo;
      a.gp += gc;
      a.gc += gf;
      b.gp += gf;
      b.gc += gc;
      if (gc > gf) {
        a.v++;
        a.pts += 3;
        b.d++;
      } else if (gc < gf) {
        b.v++;
        b.pts += 3;
        a.d++;
      } else {
        a.e++;
        b.e++;
        a.pts++;
        b.pts++;
      }
    });
  });
  return linhas.sort((x, y) => y.pts - x.pts || y.v - x.v || y.gp - y.gc - (x.gp - x.gc) || y.gp - x.gp || x.clube.localeCompare(y.clube, 'pt-BR'));
}

/** Jogos de uma rodada da liga, com o placar quando já foi jogada. */
export function jogosDaRodadaMundo(e: EstadoJogo, ligaId: string, rodada: number): { casa: string; fora: string; placar: [number, number] | null }[] {
  const viva = e.ligasMundoVivas?.[ligaId];
  if (!viva) return [];
  const cal = calendarioDaLiga(e.semente, viva.temporada, ligaId);
  return (cal.rodadas[rodada] ?? []).map(([c, f], k) => {
    const p = viva.placares[rodada]?.[k];
    return { casa: cal.clubes[c] as string, fora: cal.clubes[f] as string, placar: p === undefined ? null : [Math.floor(p / 100), p % 100] };
  });
}

/**
 * Fecha a temporada das ligas estrangeiras: joga as rodadas que faltam (se faltarem) e guarda a classificação final em
 * `e.ligasMundo`. Devolve false se o jogo não tinha as ligas em andamento (saves antigos); aí vale a simulação direta.
 */
export function finalizarLigasMundo(e: EstadoJogo): boolean {
  const vivas = e.ligasMundoVivas;
  if (!vivas || Object.keys(vivas).length === 0) return false;
  const finais: Record<string, { temporada: number; tabela: LinhaMundo[] }> = {};
  for (const [id, viva] of Object.entries(vivas)) {
    const total = calendarioDaLiga(e.semente, viva.temporada, id).rodadas.length;
    while (viva.jogadas < total) jogarRodada(e, id, viva);
    finais[id] = { temporada: viva.temporada, tabela: tabelaDaLigaMundo(e, id) as LinhaMundo[] };
  }
  e.ligasMundo = finais;
  return true;
}
